import { memo, useCallback } from 'react';
import styled from '@emotion/styled';
import type { ChartData, Chat, InputAttachment } from '@trinity/chat';
import { ChatCitationType, ChatQueryStatus, ChatResponseType } from '@trinity/chat';
import type { CustomResponseInput } from '@trinity/custom-pages-registry';
import { GenericFormWorkflow } from '@trinity/chat-components/components/Chat.Forms/GenericFormWorkflow.js';
import { ContextSelectorContainer } from '@trinity/chat-components/components/Chat.Header/ContextSelector.Container.js';
import { FileIcon } from '@trinity/chat-components/components/icons/File/index.js';
import { getCustomChatResponseDefinition } from '@trinity/custom-pages-registry';
import { AgentProgressContainer } from '../Chat.AgentProgress/AgentProgressContainer';
import { AgentProgressContext } from '../Chat.AgentProgress/context';
import { Item as AttachmentItem } from '../Chat.Attachments/Item';
import { CustomPageContextContainer } from '../CustomPageContext';
import { BasicMarkdown } from '../Markdown/Markdown';
import { AudioStatusListener } from './AudioStatusListener';
import { AttachmentsContext, AttachmentItemRendererContext } from './context';
import { KolProfileMessage } from './KolSearch.Profile';
import { AvailabilityInsightsMessage } from './KolSearch.Profile.Availability';
import { LocationForm } from './Location.Form';
import { ChartContainer } from './Message.Response.ChartContainer';
import { HcpRecommendationsSearchResponse } from './Message.Response.HcpRecommendationsSearch';
import { HcpSearchResponse } from './Message.Response.HcpSearch';
import { HcpSearchResultsResponse } from './Message.Response.HcpSearchResults';
import { HcpSelectionMessage } from './Message.Response.HcpSelection';
import { SchedulingAssistantResponse } from './Message.Response.SchedulingAssistant';
import { StreamingSystemUserMessage, SystemUserMessage } from './Message.System';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { SystemUserCancelledMessage } from './Message.System.Cancelled';
import { SystemMessageContainer } from './Message.System.Container';
import { SystemUserErrorMessage } from './Message.System.Error';
import { SystemUserInFlightResponse } from './Message.System.InProgress';
import { MessageWrapper } from './Message.System.Wrapper';
import { UserMessage } from './Message.User';
import { QueryFollowupsContainer } from './Query.FollowupsContainer';
import { QueryEventsContainer } from './QueryEventsContainer';
import { QuerySocketContainer } from './QuerySocketContainer';
import { SegmentationConfigure } from './Segmentation.Configure';
import { SegmentationDimensionSelection } from './Segmentation.DimensionSelection';
import { SegmentationEDA } from './Segmentation.EDA';
import { SegmentationExport } from './Segmentation.Export';
import { SegmentationFeatures } from './Segmentation.Features';
import { SegmentationRefinement } from './Segmentation.Refinement';
import { SegmentationResult, SegmentationResultUserMessage } from './Segmentation.Result';
import { SegmentationResults } from './Segmentation.Results';
import { SegmentationStrategy } from './Segmentation.Strategy';
import { SegmentationTagSelection } from './Segmentation.TagSelection';
import { SegmentationUpload } from './Segmentation.Upload';
import { SegmentChatSelection } from './SegmentChatSelection';
import { SmartRoutingContainer } from './SmartRouting.Container';

type Props = {
  canRegenerate: boolean;
  item: Chat.InstanceQueryItem;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function renderHcpResponses(item: Chat.InstanceQueryItem, canRegenerate: boolean) {
  const responseType = item.query.response?.type;

  if (responseType === ChatResponseType.HcpSelection) {
    return (
      <HcpSelectionMessage
        queryId={item.query.id}
        queryIdentifier={item.query.identifier}
        value={item.query.state as Chat.QueryState.HcpSelection}
        disabled={!canRegenerate}
      />
    );
  }

  if (responseType === ChatResponseType.HcpSearch) {
    return <HcpSearchResponse disabled={!canRegenerate} />;
  }

  if (responseType === ChatResponseType.HcpSearchResults) {
    return (
      <HcpSearchResultsResponse
        queryIdentifier={item.query.identifier}
        response={item.query.response}
        disabled={!canRegenerate}
      />
    );
  }

  if (responseType === ChatResponseType.HcpRecommendationsSearch) {
    return (
      <HcpRecommendationsSearchResponse
        queryIdentifier={item.query.identifier}
        response={item.query.response}
        disabled={!canRegenerate}
      />
    );
  }

  if (responseType === ChatResponseType.HcpSummary) {
    return (
      <QueryFollowupsContainer queryIdentifier={item.query.identifier} queryStatus={item.query.status}>
        <AudioStatusListener queryIdentifier={item.query.identifier} queryId={item.query.id}>
          <SystemMessageContainer citations={item.query.citations}>
            <KolProfileMessage
              followupQuestions={item.query.suggestedFollowups}
              profile={item.query.response.data.profile}
              queryId={item.query.id}
            />
          </SystemMessageContainer>
        </AudioStatusListener>
      </QueryFollowupsContainer>
    );
  }

  if (responseType === ChatResponseType.HcpAvailability) {
    return (
      <MessageContainer>
        <MessageWrapper>
          <SystemMessageContainer
            citations={item.query.citations.filter((x) => x.type === ChatCitationType.FbmsInteractions)}
          >
            <BasicSystemUserMessage>
              <AvailabilityInsightsMessage
                citations={item.query.citations}
                profile={item.query.response.data.profile}
              />
            </BasicSystemUserMessage>
          </SystemMessageContainer>
        </MessageWrapper>
      </MessageContainer>
    );
  }

  if (responseType === ChatResponseType.LocationSelection) {
    return (
      <MessageContainer>
        <MessageWrapper>
          <BasicSystemUserMessage>
            <LocationForm
              queryId={item.query.id}
              queryIdentifier={item.query.identifier}
              value={item.query.state as Chat.QueryState.LocationSelection}
              hcp={item.query.response.hcp}
              disabled={!canRegenerate}
            />
          </BasicSystemUserMessage>
        </MessageWrapper>
      </MessageContainer>
    );
  }

  if (responseType === ChatResponseType.SchedulingAssistant) {
    return (
      <MessageWrapper>
        <BasicSystemUserMessage>
          <SchedulingAssistantResponse
            queryId={item.query.id}
            state={item.query.state as Chat.QueryState.SchedulingAssistant}
          />
        </BasicSystemUserMessage>
      </MessageWrapper>
    );
  }

  if (responseType === ChatResponseType.FormWorkflow) {
    return (
      <MessageWrapper>
        <BasicSystemUserMessage>
          <ContextSelectorContainer>
            <GenericFormWorkflow
              key={`form-workflow-${item.query.id}-${item.query.identifier}`}
              queryId={item.query.id}
              queryIdentifier={item.query.identifier}
              value={item.query.state}
            />
          </ContextSelectorContainer>
        </BasicSystemUserMessage>
      </MessageWrapper>
    );
  }

  return undefined;
}

function renderCustomResponse(item: Chat.InstanceQueryItem) {
  const response = item.query.response;
  if (!response) {
    return undefined;
  }

  const definition = getCustomChatResponseDefinition(response.type);
  if (!definition || definition.renderLocation !== 'top-level') {
    return undefined;
  }

  const Component = definition.component;

  return (
    <MessageContainer>
      <MessageWrapper>
        <BasicSystemUserMessage>
          <CustomPageContextContainer>
            <Component
              queryId={item.query.id}
              queryIdentifier={item.query.identifier}
              response={response as CustomResponseInput}
            />
          </CustomPageContextContainer>
        </BasicSystemUserMessage>
      </MessageWrapper>
    </MessageContainer>
  );
}

function renderSegmentationBuilderStep(item: Chat.InstanceQueryItem) {
  const responseType = item.query.response?.type;
  const ComponentMap: Partial<
    Record<
      ChatResponseType,
      React.ComponentType<{ queryId: number; queryIdentifier: string; value: unknown }>
    >
  > = {
    [ChatResponseType.SegmentationUpload]: SegmentationUpload,
    [ChatResponseType.SegmentationConfigure]: SegmentationConfigure,
    [ChatResponseType.SegmentationEDA]: SegmentationEDA,
    [ChatResponseType.SegmentationStrategy]: SegmentationStrategy,
    [ChatResponseType.SegmentationRefinement]: SegmentationRefinement,
    [ChatResponseType.SegmentationFeatures]: SegmentationFeatures,
    [ChatResponseType.SegmentationResults]: SegmentationResults,
    [ChatResponseType.SegmentationExport]: SegmentationExport,
  };
  const Component = ComponentMap[responseType];
  if (Component) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <Component
            queryId={item.query.id}
            queryIdentifier={item.query.identifier}
            value={item.query.state}
          />
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }
  return undefined;
}

function renderSegmentationResponses(item: Chat.InstanceQueryItem, canRegenerate: boolean) {
  const responseType = item.query.response?.type;

  if (responseType === ChatResponseType.SegmentationTagsNeeded) {
    const content = (
      <MessageContainer>
        <BasicSystemUserMessage>
          <SegmentationTagSelection
            queryId={item.query.id}
            queryIdentifier={item.query.identifier}
            value={item.query.state as Chat.QueryState.SegmentationTagsNeeded}
          />
        </BasicSystemUserMessage>
      </MessageContainer>
    );
    if (!canRegenerate) return content;
    return (
      <QuerySocketContainer queryIdentifier={item.query.identifier}>
        <QueryEventsContainer queryIdentifier={item.query.identifier}>{content}</QueryEventsContainer>
      </QuerySocketContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentChatNeeded) {
    return (
      <QuerySocketContainer queryIdentifier={item.query.identifier}>
        <QueryEventsContainer queryIdentifier={item.query.identifier}>
          <MessageContainer>
            <BasicSystemUserMessage>
              <SegmentChatSelection
                queryIdentifier={item.query.identifier}
                value={item.query.state as Chat.QueryState.SegmentChatNeeded}
              />
            </BasicSystemUserMessage>
          </MessageContainer>
        </QueryEventsContainer>
      </QuerySocketContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentChatCancelled) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <div>No problem! Let me know how else I can help.</div>
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentChatSelected) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <div>{item.query.response.value}</div>
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentationQuestionsNeeded) {
    const state = item.query.state as Chat.QueryState.SegmentationQuestionsNeeded;
    const selectedTagNames = state.availableTags
      .filter((tag) => state.selectedTags.includes(tag.id))
      .map((tag) => tag.name)
      .join(', ');
    return (
      <QuerySocketContainer queryIdentifier={item.query.identifier}>
        <QueryEventsContainer queryIdentifier={item.query.identifier}>
          <MessageContainer>
            <UserMessage value={`I'd like to analyze ${selectedTagNames} tagged data.`} />
          </MessageContainer>
          <MessageContainer>
            <BasicSystemUserMessage>
              <SegmentationDimensionSelection
                queryId={item.query.id}
                queryIdentifier={item.query.identifier}
                value={item.query.state as Chat.QueryState.SegmentationQuestionsNeeded}
              />
            </BasicSystemUserMessage>
          </MessageContainer>
        </QueryEventsContainer>
      </QuerySocketContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentationCancelled) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <div>Segmentation has been cancelled.</div>
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }

  if (responseType === ChatResponseType.SegmentationComplete) {
    return (
      <QuerySocketContainer queryIdentifier={item.query.identifier}>
        <QueryEventsContainer queryIdentifier={item.query.identifier}>
          <MessageContainer>
            <UserMessage
              value={
                <SegmentationResultUserMessage
                  queryId={item.query.id}
                  queryIdentifier={item.query.identifier}
                  value={item.query.state as Chat.QueryState.SegmentationComplete}
                  citations={item.query.citations}
                />
              }
            />
          </MessageContainer>
          <MessageContainer>
            <SystemMessageContainer citations={item.query.citations}>
              <BasicSystemUserMessage>
                <SegmentationResult
                  queryId={item.query.id}
                  queryIdentifier={item.query.identifier}
                  value={item.query.state as Chat.QueryState.SegmentationComplete}
                  citations={item.query.citations}
                />
              </BasicSystemUserMessage>
            </SystemMessageContainer>
          </MessageContainer>
        </QueryEventsContainer>
      </QuerySocketContainer>
    );
  }

  const builderStep = renderSegmentationBuilderStep(item);
  if (builderStep !== undefined) return builderStep;

  return undefined;
}

function renderSmartRoutingResponse(item: Chat.InstanceQueryItem) {
  return (
    <SmartRoutingContainer
      state={item.query.state as Chat.QueryState.Value<typeof ChatResponseType.SmartRouting>}
      queryId={item.query.id}
      queryIdentifier={item.query.identifier}
    />
  );
}

export const QueryItem = memo(
  ({ canRegenerate, item }: Props) => {
    const renderSystemResponse = useCallback(() => {
      const responseType = item.query.response?.type;

      const customResult = renderCustomResponse(item);
      if (customResult !== undefined) return customResult;

      const hcpResult = renderHcpResponses(item, canRegenerate);
      if (hcpResult !== undefined) return hcpResult;

      const segResult = renderSegmentationResponses(item, canRegenerate);
      if (segResult !== undefined) return segResult;

      if (responseType === ChatResponseType.SmartRouting) {
        return renderSmartRoutingResponse(item);
      }

      return (
        <MessageContainer>
          <MessageResponse item={item.query} hasRegenerateFooter={canRegenerate} />
        </MessageContainer>
      );
    }, [item.query, canRegenerate]);

    const renderUserMessage = useCallback(() => {
      if (item.query.hideUserMessage) {
        return null;
      }

      const attachments = item.query.inputAttachments;

      return (
        <MessageContainer>
          <UserMessage
            value={
              <>
                {attachments && attachments.length > 0 && (
                  <InputAttachmentCards>
                    {attachments.map((a: InputAttachment) => (
                      <InputAttachmentCard key={a.identifier}>
                        <FileIcon extension={a.name.split('.').pop() ?? ''} size={28} />
                        <InputAttachmentCardBody>
                          <InputAttachmentCardName title={a.name}>{a.name}</InputAttachmentCardName>
                          <InputAttachmentCardSize>{formatBytes(a.sizeBytes)}</InputAttachmentCardSize>
                        </InputAttachmentCardBody>
                      </InputAttachmentCard>
                    ))}
                  </InputAttachmentCards>
                )}
                <BasicMarkdown>{item.query.value}</BasicMarkdown>
              </>
            }
          />
        </MessageContainer>
      );
    }, [item.query.value, item.query.hideUserMessage, item.query.inputAttachments]);

    return (
      <>
        {renderUserMessage()}
        {renderSystemResponse()}
      </>
    );
  },
  (prev, next) => prev.item.query === next.item.query && prev.canRegenerate === next.canRegenerate
);

type MessageResponseProps = {
  item: Chat.Query;
  hasRegenerateFooter: boolean;
};

// todo: possibly refactor to inverse the layout and have the current inner UI elements
// like variant selector and other actions contain the message response component
const MessageResponse = ({ item, hasRegenerateFooter }: MessageResponseProps) => {
  switch (item.status) {
    case ChatQueryStatus.LoadingExtended:
    case ChatQueryStatus.Loading: {
      return (
        <QuerySocketContainer queryIdentifier={item.identifier}>
          <QueryEventsContainer queryIdentifier={item.identifier}>
            <AgentProgressContainer queryIdentifier={item.identifier}>
              <SystemUserInFlightResponse />
            </AgentProgressContainer>
          </QueryEventsContainer>
        </QuerySocketContainer>
      );
    }

    case ChatQueryStatus.Error:
      return <SystemUserErrorMessage item={item} traceId={item.sentryTraceId} hasRegenerateFooter={false} />;

    case ChatQueryStatus.Streaming:
      return (
        <QuerySocketContainer queryIdentifier={item.identifier}>
          <QueryEventsContainer queryIdentifier={item.identifier}>
            <StreamingSystemUserMessage item={item} />
          </QueryEventsContainer>
        </QuerySocketContainer>
      );

    case ChatQueryStatus.Cancelled: {
      return <SystemUserCancelledMessage item={item} />;
    }

    case ChatQueryStatus.Complete:
    case ChatQueryStatus.FollowupsAvailable: {
      if (!item.response) {
        return null;
      }
      let chart: ChartData | null;
      if (item.response.type === ChatResponseType.Chart) {
        chart = item.response.data as ChartData;
      } else if (item.response.type === ChatResponseType.BIChartQuantAnalyses) {
        chart = item.response.data;
      } else {
        chart = null;
      }

      return (
        <AttachmentItemRendererContext.Provider value={AttachmentItem}>
          <AttachmentsContext.Provider value={item.attachments}>
            <QueryFollowupsContainer queryIdentifier={item.identifier} queryStatus={item.status}>
              <AudioStatusListener queryIdentifier={item.identifier} queryId={item.id}>
                <SystemMessageContainer citations={item.citations}>
                  <ChartContainer chart={chart}>
                    <AgentProgressContext.Provider value={item.agentProgress}>
                      <SystemUserMessage item={item} hasRegenerateFooter={hasRegenerateFooter} />
                    </AgentProgressContext.Provider>
                  </ChartContainer>
                </SystemMessageContainer>
              </AudioStatusListener>
            </QueryFollowupsContainer>
          </AttachmentsContext.Provider>
        </AttachmentItemRendererContext.Provider>
      );
    }
    // no default
  }
};

const MessageContainer = styled.div({
  paddingRight: 20,
  paddingLeft: 20,
});

const InputAttachmentCards = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 8,
});

const InputAttachmentCard = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  borderRadius: 8,
  border: `1px solid ${theme.palette.gray.light2}`,
  background: theme.palette.white.main,
  maxWidth: 200,
}));

const InputAttachmentCardBody = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const InputAttachmentCardName = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.black.light1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 130,
}));

const InputAttachmentCardSize = styled('span')(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.gray.dark1,
}));
